import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, withMethods } from '@ngrx/signals';
import { addEntity, removeEntity, setAllEntities, upsertEntity } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';

import { Response } from '../../../../shared/models/response.model';
import { FamilleLivrables } from '../models/famille-livrables.model';
import { FamilleLivrablesCreateRequest } from '../requests/famille-livrables-create-request';
import { FamilleLivrablesUpdateRequest } from '../requests/famille-livrables-update-request';
import { FamilleLivrablesService } from '../services/famille-livrables.service';
import { v7 as uuidv7 } from 'uuid';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { HttpErrorResponse } from '@angular/common/http';

export function avecMéthodesFamilleLivrables() {
  return signalStoreFeature(
    // Updater
    withMethods((store) => ({
      chargerFamilles: (state: { familles: FamilleLivrables[] }) => patchState(store, setAllEntities(state.familles)),
      ajouterFamille: (famille: FamilleLivrables) => patchState(store, addEntity(famille)),
      upsertFamille: (famille: FamilleLivrables) => patchState(store, upsertEntity(famille)),
      supprimerFamille: (idFamilleLivrables: string) => patchState(store, removeEntity(idFamilleLivrables)),
    })),
    withMethods((store, familleLivrablesService = inject(FamilleLivrablesService)) => ({
      charger: rxMethod<{ idCartographie: string; idRéférentiel?: string }>(
        pipe(
          switchMap((params) =>
            familleLivrablesService.trouverTous(params.idCartographie, params.idRéférentiel).pipe(
              tapResponse({
                next: (result: Response<FamilleLivrables>) => store.chargerFamilles({ familles: result.items }),
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération des familles de livrables, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      chargerToutes: rxMethod<string>(
        pipe(
          switchMap((idCartographie) =>
            familleLivrablesService.trouverTous(idCartographie).pipe(
              tapResponse({
                next: (result: Response<FamilleLivrables>) => store.chargerFamilles({ familles: result.items }),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération des familles de livrables, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      trouverParId: rxMethod<string>(
        pipe(
          switchMap((id) =>
            familleLivrablesService.trouverParId(id).pipe(
              tapResponse({
                next: (result) => store.upsertFamille(result),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération de la famille de livrables, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      créer: rxMethod<{
        request: FamilleLivrablesCreateRequest;
        next: () => void;
        error: () => void;
      }>(
        pipe(
          switchMap(({ request, next, error }) =>
            familleLivrablesService.créer(request).pipe(
              tapResponse({
                next: () => next(),
                error: (err: HttpErrorResponse) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error.detail ??
                        `Un problème est survenu lors de la création des familles de livrables, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  error();
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{
        idFamilleLivrables: string;
        request: FamilleLivrablesUpdateRequest;
        next: () => void;
        error: () => void;
      }>(
        pipe(
          switchMap(({ idFamilleLivrables, request, next, error }) =>
            familleLivrablesService.modifier(idFamilleLivrables, request).pipe(
              tapResponse({
                next: () => next(),
                error: (err: HttpErrorResponse) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error.detail ??
                        `Un problème est survenu lors de la modification de la famille de livrables, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  error();
                },
              }),
            ),
          ),
        ),
      ),
      supprimer: rxMethod<{ idFamilleLivrables: string; next: () => void }>(
        pipe(
          switchMap(({ idFamilleLivrables, next }) =>
            familleLivrablesService.supprimer(idFamilleLivrables).pipe(
              tapResponse({
                next: () => {
                  store.supprimerFamille(idFamilleLivrables);
                  next();
                },
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la suppression de la famille de livrables, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
    })),
  );
}
